/**
 * ToastNotification - Toast Notification Component
 *
 * A CVA-based toast notification component with:
 * - Type variants (success/error/warning/info)
 * - Position variants (top-right/top-left/bottom-right/bottom-left)
 * - Auto-dismiss with configurable duration
 * - Undo button with timeout
 *
 * @module features/ai-button/components/ToastNotification
 * @see SPEC-019 Task 1.3
 * @see Requirements 4.6, 4.7, 4.8, 5.1, 5.2, 5.3, 5.6, 8.7
 */

import React, { useEffect, useState, useCallback } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/cn';
import { useTypedTranslation } from '../../../i18n/useTypedTranslation';

// ============================================================================
// CVA Variants
// ============================================================================

/**
 * ToastNotification variants using CVA (Class Variance Authority)
 * Defines type and position variations
 */
export const toastVariants = cva(
  // Base styles (always applied)
  [
    'fixed z-50',
    'flex items-start gap-3',
    'min-w-[300px] max-w-[400px]',
    'p-4 rounded-lg shadow-lg',
    'border-l-4',
    'animate-in fade-in slide-in-from-top-2',
  ],
  {
    variants: {
      type: {
        success: ['bg-green-50', 'border-green-500', 'text-green-800'],
        error: ['bg-red-50', 'border-red-500', 'text-red-800'],
        warning: ['bg-yellow-50', 'border-yellow-500', 'text-yellow-800'],
        info: ['bg-blue-50', 'border-blue-500', 'text-blue-800'],
      },
      position: {
        'top-right': ['top-4', 'right-4'],
        'top-left': ['top-4', 'left-4'],
        'bottom-right': ['bottom-4', 'right-4'],
        'bottom-left': ['bottom-4', 'left-4'],
      },
    },
    defaultVariants: {
      type: 'info',
      position: 'top-right',
    },
  }
);

/** Exported type for ToastNotification variants */
export type ToastVariants = VariantProps<typeof toastVariants>;

// ============================================================================
// Types
// ============================================================================

export interface ToastNotificationProps extends ToastVariants {
  /** Toast message to display */
  message: string;
  /** Whether to auto-dismiss the toast */
  autoDismiss?: boolean;
  /** Auto-dismiss duration in milliseconds (default: 5000) */
  autoDismissMs?: number;
  /** Callback when toast is dismissed */
  onDismiss?: () => void;
  /** Whether to show Undo button */
  showUndo?: boolean;
  /** Undo timeout in milliseconds (default: 10000) */
  undoTimeoutMs?: number;
  /** Callback when Undo is clicked */
  onUndo?: () => void;
  /** Additional className */
  className?: string;
}

// ============================================================================
// Sub-components: Icons
// ============================================================================

const SuccessIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    data-testid="success-icon"
    className={cn('w-5 h-5 text-green-500', className)}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
      clipRule="evenodd"
    />
  </svg>
);

const ErrorIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    data-testid="error-icon"
    className={cn('w-5 h-5 text-red-500', className)}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z"
      clipRule="evenodd"
    />
  </svg>
);

const WarningIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    data-testid="warning-icon"
    className={cn('w-5 h-5 text-yellow-500', className)}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
      clipRule="evenodd"
    />
  </svg>
);

const InfoIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    data-testid="info-icon"
    className={cn('w-5 h-5 text-blue-500', className)}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z"
      clipRule="evenodd"
    />
  </svg>
);

const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={cn('w-4 h-4', className)}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z"
      clipRule="evenodd"
    />
  </svg>
);

// ============================================================================
// Icon Map
// ============================================================================

const ICON_MAP = {
  success: SuccessIcon,
  error: ErrorIcon,
  warning: WarningIcon,
  info: InfoIcon,
} as const;

// ============================================================================
// Component
// ============================================================================

/**
 * ToastNotification Component
 *
 * A toast notification with auto-dismiss and undo functionality.
 *
 * @example
 * ```tsx
 * <ToastNotification
 *   type="success"
 *   message="Changes applied successfully"
 *   showUndo
 *   onUndo={() => handleUndo()}
 *   onDismiss={() => setShowToast(false)}
 * />
 * ```
 */
export const ToastNotification: React.FC<ToastNotificationProps> = ({
  type = 'info',
  position = 'top-right',
  message,
  autoDismiss = false,
  autoDismissMs = 5000,
  onDismiss,
  showUndo = false,
  undoTimeoutMs = 10000,
  onUndo,
  className,
}) => {
  const { t } = useTypedTranslation();
  const [isUndoDisabled, setIsUndoDisabled] = useState(false);

  // Auto-dismiss timer
  useEffect(() => {
    if (!autoDismiss || !onDismiss) return;

    const timer = setTimeout(() => {
      onDismiss();
    }, autoDismissMs);

    return () => clearTimeout(timer);
  }, [autoDismiss, autoDismissMs, onDismiss]);

  // Undo timeout timer
  useEffect(() => {
    if (!showUndo) return;

    const timer = setTimeout(() => {
      setIsUndoDisabled(true);
    }, undoTimeoutMs);

    return () => clearTimeout(timer);
  }, [showUndo, undoTimeoutMs]);

  const handleDismiss = useCallback(() => {
    onDismiss?.();
  }, [onDismiss]);

  const handleUndo = useCallback(() => {
    if (!isUndoDisabled) {
      onUndo?.();
    }
  }, [isUndoDisabled, onUndo]);

  const IconComponent = ICON_MAP[type ?? 'info'];

  return (
    <div role="alert" className={cn(toastVariants({ type, position }), className)}>
      {/* Icon */}
      <div className="flex-shrink-0">
        <IconComponent />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{message}</p>

        {/* Undo button */}
        {showUndo && (
          <button
            type="button"
            onClick={handleUndo}
            disabled={isUndoDisabled}
            className={cn(
              'mt-2 text-sm font-medium underline',
              'hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-2',
              isUndoDisabled && 'opacity-50 cursor-not-allowed'
            )}
            aria-label={t('ai.toast.undo')}
          >
            {t('ai.toast.undo')}
          </button>
        )}
      </div>

      {/* Dismiss button */}
      <button
        type="button"
        onClick={handleDismiss}
        className={cn(
          'flex-shrink-0 p-1 rounded-full',
          'hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-offset-2',
          'transition-colors'
        )}
        aria-label="Dismiss"
      >
        <CloseIcon />
      </button>
    </div>
  );
};
